/* eslint-disable react/jsx-no-bind */
import classNames from "classnames";
import Badge from "~/components/shared/Badge";
import { NavLink, useMatch } from "react-router-dom";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import { urlForView } from "~/components/build/Show/lib/urlForView";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";
import { useMemo } from "react";
import FailedTestExecutionsIndicator from "~/components/job/Job/FailedTestExecutionsIndicator";
import { motion } from "framer-motion";
import Count from "~/components/build/Show/components/Count";
import { useQuery } from "@tanstack/react-query";
import { testEngineInfoQueryOptions } from "~/components/build/Show/lib/queries/testEngineInfo";
import { isBuildFinished } from "~/components/build/Show/lib/types/Build";

const BuildNavLink = ({ children, view }: { view: BuildView; children: React.ReactNode }) => {
  const { build } = useBuild();
  const params = useQueryParams();
  const to = urlForView(build.path, view, params);
  const isActive = useMatch(to.split("?")[0] + "/*");

  return (
    <NavLink
      to={to}
      className="btn btn-tab relative px-2 text-sm text-current transition-colors focus:text-current"
    >
      {children}
      {isActive && (
        <motion.div
          layoutId="build-nav-link-active-indicator"
          className="absolute left-0 right-0 top-full h-[3px] bg-purple-600"
          transition={{ type: "spring", duration: 0.2, bounce: 0 }}
        />
      )}
    </NavLink>
  );
};

function showTestsTabPill(testTabUrl: string | null | undefined) {
  // The "new" pill is cleared when the tests tab turbo frame is loaded in
  // the V2TestEngine component.
  const isMarketingUrl = testTabUrl?.includes("marketing");
  const isPillDismissed = localStorage.getItem("hide-test-tab-pill") === "true";
  return isMarketingUrl && !isPillDismissed;
}

function BuildContentNavigation() {
  const { build } = useBuild();

  // When lazy load test engine flag is enabled, test engine data is fetched separately
  const { data: testEngineInfo } = useQuery({
    ...testEngineInfoQueryOptions(build),
    refetchInterval: ({ state }) => {
      // When we use JSRoutes, we should use Route matchers for account_analytics_build_tests_path
      // But for now, hardcode the result: /organizations/:account_id/analytics/builds/:build_id
      if (state.data?.testEngine.testTabUrl?.includes(`/analytics/builds/${build.id}`)) {
        // We've got a test engine build URL, nothing left to do
        return false;
      }
      // We've probably been given the marketing URL or null, in which case, let's keep fetching slowly
      return 10_000;
    },
    retry: false, // Never retry - polling will continue via refetchInterval
    enabled: Features.ProgressiveBuildShowLazyLoadTestEngine && !isBuildFinished(build),
  });

  // Resolve test tab URL from lazy loaded data (progressive) or initial payload (legacy)
  const testTabUrl = Features.ProgressiveBuildShowLazyLoadTestEngine
    ? testEngineInfo?.testEngine?.testTabUrl
    : build.testEngine.testTabUrl;

  const navItems = useMemo(() => {
    const items: {
      view: BuildView;
      label: string;
      indicator?: React.ReactNode;
      pill?: React.ReactNode;
      count?: React.ReactNode;
    }[] = [
      { view: BuildView.Summary, label: "Summary" },
      { view: BuildView.Steps, label: "Steps" },
    ];

    const annotationCount = Object.values(build.annotationCountsByStyle).reduce(
      (acc, count) => acc + count,
      0,
    );

    items.push({
      view: BuildView.Annotations,
      label: "Annotations",
      count: annotationCount > 0 && (
        <span className="ml-2">
          <Count data-testid="annotations-count">{annotationCount}</Count>
        </span>
      ),
    });

    if (testTabUrl && !Features.TestEngineDisableBuildPageTestTab) {
      items.push({
        view: BuildView.Tests,
        label: "Tests",
        indicator: (
          <FailedTestExecutionsIndicator build={build} translateX="1px" translateY="8px" />
        ),
        pill: showTestsTabPill(testTabUrl) && (
          <span className="badge badge--outline tabular-numerals ml-1">New</span>
        ),
      });
    }

    return items;
  }, [build, testTabUrl]);

  if (!Features.BuildSidebar) {
    return <ClassicNavigation />;
  }

  return (
    <nav data-testid="BuildContentNavigation" className="flex">
      {navItems.map(({ label, view, indicator, pill, count }) => (
        <BuildNavLink key={view} view={view}>
          {indicator}
          {label}
          {pill}
          {count}
        </BuildNavLink>
      ))}
    </nav>
  );
}

/**
 * Navigation for users without BuildSidebar feature
 */
function ClassicNavigation() {
  const { build, store } = useBuild();

  const listHref = build.path;
  const testsHref = "./tests";
  const canvasHref = "./canvas";
  const waterfallHref = "./waterfall";
  const showBuildTests = build.testEngine.testTabUrl && !Features.TestEngineDisableBuildPageTestTab;

  return (
    <nav className="Tablist border-gray mb-[-1px] flex border-b" data-testid="buildViewTabsNav">
      <NavLink
        to={listHref}
        end={true}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Jobs</span>
      </NavLink>

      <NavLink
        to={canvasHref}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Canvas</span>
      </NavLink>

      <NavLink
        to={waterfallHref}
        className={({ isActive }) =>
          classNames("btn btn-tab text-current focus:text-current", {
            "btn-tab--active": isActive,
          })
        }
      >
        <span>Waterfall</span>
        {!store.waterfallAvailable && (
          <Badge outline={false} className="white bg-purple">
            Upgrade
          </Badge>
        )}
      </NavLink>

      {showBuildTests && (
        <NavLink
          to={testsHref}
          className={({ isActive }) =>
            classNames("btn btn-tab relative text-current focus:text-current", {
              "btn-tab--active": isActive,
            })
          }
        >
          <FailedTestExecutionsIndicator build={build} />
          <span>Tests</span>
          {showTestsTabPill(build.testEngine.testTabUrl) && (
            <span className="badge badge--outline tabular-numerals ml-1">New</span>
          )}
        </NavLink>
      )}
    </nav>
  );
}

export default BuildContentNavigation;
